/**
 * Responsible for creating a slider zoom or in other words a carousel where the
 * images in the carousel can be interacted with so that they can be zoomed in
 * @module modules/zoom/sliderZoom
 */
import $ from 'jquery';
import _get from 'lodash/get';
import $script from 'scriptjs';
import '../slick-carousel';

import customPaging from '../slick/custom-paging';
import { get } from '../../lib/props';
import imageZoom from './image-zoom';
import Keyboard from '../keyboard';
/* Mock Content Start UX-20630 */
import customVideoPlayer from '../abtest/custom-video-player';
/* Mock Content End UX-20630 */

const HAMMERJS_FALLBACK = 'https://ajax.googleapis.com/ajax/libs/hammerjs/2.0.8/hammer.min.js';
const renderNoop = () => '';

/**
 * Makes a request for HammerJS.
 * @private
 * @return {Promise} Denotes when the request has been successfully executed
 */
const loadHammerJSFunc = () =>
  new Promise((resolve) => {
    $script(get('scripts.hammerJS', HAMMERJS_FALLBACK), () => {
      resolve(window.Hammer);
    });
  });

/**
 * Defines the options which can be provided for the image zoom.
 * @type {Object}
 */
const DEFAULTS = {
  loadHammerJS: loadHammerJSFunc,
  startingIndex: 0,
  speed: 300,
  slideFunc: num => num,
  menuRender: renderNoop,
  beforeContentRender: renderNoop,
  didRender: () => {},
  beforeUnmount: () => {},
  didUnmount: () => {},
  didSlideTransition: (prev, current) => ({ prev, current }),
  zoomClickListener: () => { },
  noImages: '',
  trapFocus: '',
  trapFocusFunc: Keyboard.trapFocus.bind(Keyboard),
  releaseFocusFunc: Keyboard.releaseFocus.bind(Keyboard),
};

/**
 * Defines the id for the close element.
 * @type {String}
 */
export const CLOSE_ID = 'close-zoom-menu';

/**
 * Defines the id for the zoom slider.
 * @type {String}
 */
export const ZOOM_SLIDER_ID = 'zoom-slider';

/**
 * Defines the id for the zoom slider.
 * @type {String}
 */
export const ZOOM_THUMB_ID = 'zoom-thumb';

/**
 * Defines the content className
 * @type {String}
 */
export const CONTENT_CLASSNAME = 'zoom-slider__content';

/**
 * Binds an event to the close element
 * @private
 * @param  {Function} callback - The event handler for the close element
 */
const bindClose = (callback) => {
  const $closeButton = $(document.getElementById(CLOSE_ID));

  // Ensure the close button exists before binding
  if ($closeButton.length) {
    // Bind click event to the close button
    $closeButton.on('click.closeZoom', callback);
  } else {
    console.warn(`Element with ID "${CLOSE_ID}" not found.`);
  }

  // Bind keydown event to the document for the Escape key
  $(document).on('keydown.closeZoom', (e) => {
    if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
      if (!$('.dialog__container').is(':visible')) return;
      e.preventDefault();
      callback();
    }
  });
};

const logCloseZoom = () => {
  if (typeof window.logEngagement === 'function') {
    window.logEngagement(window.location.href, 'pdp zoom modal', 'close zoom');
  }
};

/**
 * Closes the dialog.
 * @private
 * @param  {Dialog} dialog - Instance of a dialog
 * @param  {Function} didUnmount - Called once the dialog has closed
 */
const closeDialog = (didUnmount) => {
  // Unbind the click and keydown events on close
  $('.image-carousel').off('click.product-zoom');
  $('.image-carousel').off('keydown.product-zoom');

  // Focus back on the parent element
  $('.image-carousel').focus();

  didUnmount();
  logCloseZoom();
};

/**
 * Stupid helper that concats the element id based on an id for a slide.
 * @private
 * @param  {string} id - The id of the slide being requested
 * @return {string} - A slide id
 */
const getSlideId = id => `zoom-slider__item-${id}`;

/**
 * Obtains a slide or image basd on the id provided in the template.
 * @private
 * @param  {number} id - The id of the slide being requested
 * @return {HTMLElement} An element representing a slide or image
 */
const getSlide = id => document.getElementById(getSlideId(id));

/**
 * Executes in the future the close callback. The callback is responsible for
 * clean up to ensure nothing is left in memory.
 * @private
 * @param  {jQuery} $zoomSlider
 * @param  {Hammer} hammer - An instance of Hammer from hammerjs
 * @param  {Function} didUnmount - Used to denote when closed
 * @return {Function} Callback function used for the event
 */
const teardownFull = ($zoomSlider, hammer, beforeUnmount,
  didUnmount, zoomClickListener) => () => {
    beforeUnmount();
    hammer.destroy();
    $zoomSlider.slick('unslick');
    // Properly close modal first
    $.modal.close();
    // Remove the modal HTML from the DOM
    $('#zoom-modal').remove();
    closeDialog(didUnmount);
    zoomClickListener();
    $(document).off('keydown.closeZoom');
    // bring back NTF
    $('.js-fixed-corner').show();
  };

/**
 * Simple rendering when no images are found.
 * @private
 * @param  {String} noImages - Text indicating no images were found
 * @return {string} Template containing content around no visible images
 */
const renderNoImages = ({ noImages }) => `
  <p class="${CONTENT_CLASSNAME}">${noImages}</p>
`;

/**
 * Template containing what is to be rendered in the dialog.
 * @private
 * @param  {array} images - A list of images
 * @param  {Function} [menuRender=renderNoop] - Used for additional rendering
 * @param  {Function} [beforeContentRender=renderNoop] - Optional rendering
 * before the main content
 * @return {String} A fully rendered template as a string
 */
const renderDialog = ({
  images,
  imagesRetina = [],
  videos,
  noImages,
  menuRender = renderNoop,
  beforeContentRender = renderNoop,
}) => `
    ${beforeContentRender()}
    <div id="zoom-modal" class="modal">
          <h2 class="modal__hed u-screen-reader">Zoom Modal</h2>
          <div id="zoom-menu" class="zoom-menu">
      <span class="zoom-menu__extra">${menuRender()}</span>
      <button id="${CLOSE_ID}" class="js-modal-close zoom-menu__close">
        <span class="icon icon--md icon--x-icon">
          <svg aria-hidden="true" focusable="false" viewBox="0 0 16 16">
            <path d="M9.06 8l2.72-2.72A.75.75 0 0 0 10.72 4.22L8 6.94 5.28 4.22A.75.75 0 0 0 4.22 5.28L6.94 8l-2.72 2.72A.75.75 0 1 0 5.28 11.78L8 9.06l2.72 2.72a.75.75 0 0 0 1.061-1.061z"/>
          </svg>
        </span>
      </button>
    </div>

    <div id="${ZOOM_SLIDER_ID}" class="zoom-slider u-margin-l--none">
      ${
        images.length
          ? images.map((src2x, i) => {
            // Keep your existing mp4 handling
            if (src2x.includes('.mp4')) {
              /* Mock Content Start UX-20630 */
              // No `muted` attribute: the zoom video is skinned with
              // customVideoPlayer (videoMuted:false) on land so it plays with
              // sound. See applyZoomVideoSkin below.
              return `<div><video class="zoom-slider__item" id="${getSlideId(i)}" data-video="true" loop='loop' playsinline>
                <source src="${src2x}" type="video/mp4">
              </video></div>`;
              /* Mock Content End UX-20630 */
            }

            const src3x = imagesRetina[i]; // 3x candidate
            const srcset = src3x
              ? `${src2x} 2x, ${src3x} 3x`
              : `${src2x} 2x`;

            return `<div><img src="${src2x}" srcset="${srcset}" class="zoom-slider__item" id="${getSlideId(i)}"></div>`;
          }).join('')
          : renderNoImages({ noImages })
      }

      ${
        videos.length
          ? videos.map((video, i) =>
              `<li><video class="zoom-slider__item" id="${getSlideId(i + images.length)}" data-video="true" controls>
                <source src="${video}" type="video/mp4">
              </video></li>`,
            ).join('')
          : ''
      }
    </div>
  `;

/**
 * Merges or concats options to be passed along to slick.
 * @private
 * @param  {Object} [options={}] - Options sent along to slick
 * @return {Object} A mergingin of options for slick
 */
const mergeSliderOptions = (options = {}) =>
  $.extend({
    arrows: false,
    dots: true,
    infinite: true,
    slidesToShow: 1,
    rtl: _get(window, 'rcProps.isRtl', false),
    appendDots: '#zoom-menu',
    dotsClass: 'slider-dots__menu image-carousel__dots',
    slide: 'li',
    customPaging,
    touchThreshold: 20,
  }, options);

// When the slide is changing
function playPauseVideo(slick, control) {
  const currentSlide = slick.find('.slick-current');
  const player = currentSlide.find('iframe').get(0) || currentSlide.find('video').get(0);

  if (!player) return;

  if (player !== null) {
    if (control === 'play') {
      player.play();
    } else {
      player.pause();
    }
  }
}

/* Mock Content Start UX-20630 */

const applyZoomVideoSkin = (videoEl) => {
  if (!videoEl || videoEl.tagName !== 'VIDEO' || videoEl.dataset.skinned) {
    return;
  }
  videoEl.dataset.skinned = 'true';
  // Reuse the gallery trigger's hidden title/caption for the expand-to-read text.
  const $trigger = $('.js-mobile-video-trigger[data-video-url]').first();
  customVideoPlayer(videoEl, {
    videoMuted: false,
    hideMute: true, // renders the mute/volume button (flag is inverted)
    $originalTrigger: $trigger,
  });
};
/* Mock Content End UX-20630 */

const focusOnStartingIndex = (startingIndex) => {
  if ($('.focus-active').length) {
    $('.horizontal-scroll__item').eq(startingIndex).focus();
  }
};

/**
 * Responsible for rendering a slider zoom. More specifically, it will create
 * a dialog/modal such that n number of images are used for creating a slider.
 * On top of that, each image will have the ability for pinch/zoom/moving
 * around an image such that the image can be examined by the end user.
 * @see DEFAULTS
 * @param {array} images - A list of images used for the slider and zooming
 * @param {Object} options - Additional hooks to interact with the slider zoom
 * @return {Promise} Denotes when the slier zoom has been fully rendered
 */
export default function sliderZoom(images, options = {}) {
  const {
    startingIndex,
    speed,
    slideFunc,
    menuRender,
    didRender,
    beforeUnmount,
    didUnmount,
    loadHammerJS,
    didSlideTransition,
    noImages,
    zoomClickListener,
    beforeContentRender,
  } = $.extend({}, DEFAULTS, options);
  const videos = options.videos || [];
  return loadHammerJS().then((Hammer) => {
    const output = renderDialog({ images, videos, menuRender, noImages, beforeContentRender });

    // hide NTF
    $('.js-fixed-corner').hide();

    $('#zoom-modal').remove();

    const $modalHtml = $(output);
    $modalHtml.appendTo('body');

    $.modal({
      type: 'full',
      open: 'zoom-modal',
      triggerOpen: true,
    });

    didRender();

    if (!images.length) {
      bindClose(() => {
        closeDialog(didUnmount);
      });
      return;
    }

    slideFunc(startingIndex);

    const $zoomSlider = $(document.getElementById(ZOOM_SLIDER_ID));
    let currentSliderItem = getSlide(startingIndex);
    let hammerInstance = imageZoom(Hammer, currentSliderItem);
    let currentNum = startingIndex;
    let previous;
    let isVideo = false;

    const dotLength = $zoomSlider.children().length;

    const mergeSliderOptionsParams = {
      speed,
      initialSlide: startingIndex,
      dots: (dotLength > 1),
    };

    $zoomSlider
      .on('init', (evt, slick) => {
        slick.$slides.each((i, slide) => {
          if ($(slide).children('video').length) {
            $('.zoom-menu .image-carousel__dots li a').eq(i).addClass('image-carousel__dot-button--vid');
          }
        });
        images.forEach((value, index) => {
          // if slide is video type, change the pagination to
          // show the play button instead of the default circle
          if (value.toLowerCase().endsWith('.mp4')) {
            $('#zoom-menu .slider-dots__menu li button').eq(index).addClass('image-carousel__dot-button--vid');
          }
        });
        playPauseVideo($zoomSlider, 'play');
      })
      .on('afterChange', (evt, slide, currentSlide) => {
        // when at the beginning or end, do not reset zooming
        if (currentNum === currentSlide) {
          return;
        }
        currentNum = currentSlide;
        slideFunc(currentNum);
        previous = currentSliderItem;
        hammerInstance.destroy();
        beforeUnmount();

        currentSliderItem = getSlide(currentNum);
        isVideo = $(currentSliderItem).data('video');
        if (!isVideo) {
          hammerInstance = imageZoom(Hammer, currentSliderItem);
        } else {
          /* Mock Content Start UX-20630 */
          applyZoomVideoSkin(currentSliderItem);
          /* Mock Content End UX-20630 */
        }

        // use a setTimeout instead of the afterChange event since it'll
        // provide to long of a delay between swipe change when the image
        // has been zoomed in too much, yeah, a whole lot of edge cases
        // happening here.
        setTimeout(() => {
          /* eslint-disable no-param-reassign */
          previous.style.transform = 'none';
          /* eslint-enable no-param-reassign */
          didSlideTransition(previous, currentSliderItem);
        }, speed);

        // video play/pause
        playPauseVideo($zoomSlider, 'pause');
      }).on('beforeChange', (event, slick) => {
        const $slick = $(slick.$slider);
        playPauseVideo($slick, 'pause');
      }).on('afterChange', (event, slick) => {
        const $slick = $(slick.$slider);
        playPauseVideo($slick, 'play');
      });

    $zoomSlider.slick(mergeSliderOptions(mergeSliderOptionsParams));

    focusOnStartingIndex(startingIndex);
    bindClose(teardownFull($zoomSlider, hammerInstance, beforeUnmount,
      didUnmount, zoomClickListener));
  });
}



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/zoom/slider-zoom.js